import React, { useMemo, useState } from 'react';
import { getVideoPoster } from '../../utils/media';
import { Sparkle } from '../brand/Jaipur';
import { Eyebrow, ReelTile, getInitials, useJson } from './shared';
import './home.css';

/** Founder-owned brands are not client testimonials. */
const EXCLUDED_BRANDS = ['golead', 'go4lead', 'aarkin'];
const MAX_REVIEWS = 8;

const FRAME_ACCENTS = [
  { bar: 'text-apn-orange', sticker: 'bg-apn-orange text-neutral-950', quote: 'text-apn-orange' },
  { bar: 'text-apn-pink', sticker: 'bg-apn-pink text-neutral-950', quote: 'text-apn-pink' },
  { bar: 'text-apn-indigo', sticker: 'bg-apn-ink text-apn-bg', quote: 'text-apn-indigo' },
  { bar: 'text-apn-saffron', sticker: 'bg-apn-saffron text-neutral-950', quote: 'text-apn-saffron' },
];

const normalizeBrand = (role) => String(role || '').toLowerCase().replace(/[^a-z0-9]/g, '');

/** Accepts the review video under any of the names an admin form might use. */
const videoOf = (r) => {
  const url = r.videoUrl || r.videoSrc || (r.video && typeof r.video === 'object' ? r.video.src : r.video);
  return typeof url === 'string' && url.trim() ? url.trim() : null;
};

function Avatar({ review }) {
  const img = typeof review.imageUrl === 'string' && review.imageUrl.trim() ? review.imageUrl.trim() : null;
  if (img) {
    return <img src={img} alt="" loading="lazy" decoding="async" className="h-11 w-11 rounded-full object-cover" />;
  }
  return (
    <span aria-hidden="true" className="flex h-11 w-11 items-center justify-center rounded-full bg-apn-ink font-display text-sm font-extrabold text-apn-bg">
      {getInitials(review.name)}
    </span>
  );
}

function ReviewFrame({ review, index, soundOn, onToggleSound }) {
  const accent = FRAME_ACCENTS[index % FRAME_ACCENTS.length];
  const video = videoOf(review);
  const brand = review.role || '';

  return (
    <li className="snap-start shrink-0">
      <figure className="relative flex aspect-[9/16] h-[min(30rem,72svh)] flex-col overflow-hidden rounded-[28px] border border-apn-line bg-apn-surface text-apn-ink">
        {video ? (
          <ReelTile
            src={video}
            poster={getVideoPoster(video)}
            title={`${review.name}${brand ? `, ${brand}` : ''}`}
            soundOn={soundOn}
            onToggleSound={onToggleSound}
            className="absolute inset-0"
          />
        ) : null}

        <div aria-hidden="true" className={`relative z-10 flex gap-1 px-3 pt-3 ${video ? 'text-white' : accent.bar}`}>
          <span className="home-story-bar flex-1" />
          <span className="home-story-bar flex-1 opacity-30" />
          <span className="home-story-bar flex-1 opacity-30" />
        </div>

        <figcaption className={`relative z-10 flex items-center gap-3 px-4 pt-3 ${video ? 'pointer-events-none text-white' : ''}`}>
          <Avatar review={review} />
          <span className="min-w-0">
            <span className="block truncate font-semibold leading-tight">{review.name}</span>
            {brand ? <span className={`block truncate text-sm ${video ? 'text-white/80' : 'text-apn-muted'}`}>{brand}</span> : null}
          </span>
        </figcaption>

        {video ? (
          // Quote is available to screen readers; the video carries it visually.
          <blockquote className="sr-only">{review.feedback}</blockquote>
        ) : (
          <>
            <div className="relative flex flex-1 flex-col justify-center px-5 sm:px-6">
              <span aria-hidden="true" className={`font-display text-7xl font-extrabold leading-[0.6] ${accent.quote}`}>
                “
              </span>
              <blockquote className="mt-2 font-display text-[1.35rem] font-semibold leading-[1.2] tracking-[-0.01em] sm:text-2xl">
                {String(review.feedback || '').replace(/\s*\n\s*/g, ' ')}
              </blockquote>
            </div>
            {brand ? (
              <div className="px-4 pb-5">
                <span className={`inline-flex -rotate-3 items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-extrabold uppercase tracking-wide ${accent.sticker}`}>
                  <Sparkle className="h-3.5 w-3.5" />
                  {brand}
                </span>
              </div>
            ) : null}
          </>
        )}
      </figure>
    </li>
  );
}

export default function VerticalTestimonials() {
  const { data, error } = useJson('/api/reviews');
  const [soundId, setSoundId] = useState(null);

  const reviews = useMemo(() => {
    const list = data && Array.isArray(data.reviews) ? data.reviews : [];
    return list
      .filter((r) => r && r.feedback && !EXCLUDED_BRANDS.includes(normalizeBrand(r.role)))
      .slice(0, MAX_REVIEWS);
  }, [data]);

  if (error || (data && !reviews.length)) return null;

  return (
    <section aria-labelledby="home-reviews-title" className="overflow-x-clip bg-apn-bg py-14 sm:py-20 short:py-10">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Eyebrow>Founders, in their words</Eyebrow>
        <h2 id="home-reviews-title" data-animate="split-heading" className="mt-3 max-w-3xl font-display text-4xl font-extrabold leading-[0.98] tracking-[-0.03em] text-apn-ink sm:text-5xl lg:text-6xl">
          Don’t take our word for it.
        </h2>
      </div>

      <ul
        aria-label="Client testimonials (scrolls sideways)"
        tabIndex={0}
        className="home-scroller focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-inset focus-visible:ring-apn-orange mt-8 flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-px-4 px-4 pb-4 sm:scroll-px-6 sm:px-6 lg:scroll-px-[max(2rem,calc((100vw-80rem)/2+2rem))] lg:px-[max(2rem,calc((100vw-80rem)/2+2rem))]"
      >
        {!data
          ? [0, 1, 2, 3].map((i) => (
              <li key={i} aria-hidden="true" className="snap-start shrink-0">
                <div className="aspect-[9/16] h-[min(30rem,72svh)] animate-pulse rounded-[28px] bg-apn-surface-2" />
              </li>
            ))
          : reviews.map((r, i) => {
              const id = r._id || `${r.name}-${i}`;
              return (
                <ReviewFrame
                  key={id}
                  review={r}
                  index={i}
                  soundOn={soundId === id}
                  onToggleSound={() => setSoundId((cur) => (cur === id ? null : id))}
                />
              );
            })}
      </ul>
    </section>
  );
}
